import { useMemo, useState } from "react";
import { Card, fmt } from "@/components/impact/ImpactWidgets";

const INPUTS = [
  ["Monthly new leads", "leads", 100, 50000, 100],
  ["Current FTD rate (%)", "ftdRate", 1, 40, 1],
  ["Average FTD (€)", "avgFtd", 100, 10000, 100],
  ["Eligible leakage rate (%)", "leakage", 1, 60, 1],
  ["Illustrative recovery rate (%)", "recovery", 1, 60, 1],
  ["Monthly platform cost (€, simulated)", "cost", 500, 50000, 500],
];

export default function RoiCalculator() {
  const [calc, setCalc] = useState({ leads: 10000, ftdRate: 8, avgFtd: 1500, leakage: 20, recovery: 15, cost: 4000 });

  const out = useMemo(() => {
    const incFtds = Math.round(calc.leads * (calc.leakage / 100) * (calc.recovery / 100));
    const incDeposits = incFtds * calc.avgFtd;
    const roi = calc.cost > 0 ? incDeposits / calc.cost : 0;
    return { incFtds, incDeposits, roi };
  }, [calc]);

  return (
    <Card title="ROI Model" testId="roi-calculator" className="border-amber-500/30">
      <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm px-3.5 py-2.5 mb-4 text-[11px] font-mono text-amber-400" data-testid="roi-calculator-disclaimer">
        ILLUSTRATIVE ROI MODEL — NOT A FORECAST OR GUARANTEE. Adjust the simulated assumptions to model your own operation. Deposits are not broker revenue.
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          {INPUTS.map(([label, key, min, max, step]) => (
            <div key={key}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="text-zinc-400">{label}</span>
                <span className="font-mono text-zinc-100">{calc[key].toLocaleString()}</span>
              </div>
              <input
                type="range" min={min} max={max} step={step} value={calc[key]}
                data-testid={`roi-input-${key}`}
                onChange={(e) => setCalc({ ...calc, [key]: Number(e.target.value) })}
                className="w-full accent-emerald-500"
              />
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <div className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Potential incremental FTDs</div>
            <div className="font-mono text-2xl font-bold text-zinc-100 mt-1" data-testid="roi-out-ftds">{out.incFtds.toLocaleString()}</div>
            <div className="text-[10px] font-mono text-zinc-600 mt-1">leads × leakage rate × recovery rate</div>
          </div>
          <div className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Potential incremental deposit value</div>
            <div className="font-mono text-2xl font-bold text-zinc-100 mt-1" data-testid="roi-out-deposits">{fmt(out.incDeposits)}</div>
            <div className="text-[10px] font-mono text-zinc-600 mt-1">incremental FTDs × average FTD · assisted deposit value, not broker revenue</div>
          </div>
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-4">
            <div className="text-[10px] text-emerald-500 uppercase tracking-wider">Illustrative ROI multiple</div>
            <div className="font-mono text-2xl font-bold text-emerald-400 mt-1" data-testid="roi-out-multiple">{out.roi.toFixed(1)}×</div>
            <div className="text-[10px] font-mono text-zinc-600 mt-1">incremental deposit value ÷ platform cost</div>
          </div>
        </div>
      </div>
    </Card>
  );
}
